/**
 * Keyboard Navigation Helper
 * Shared utility functions for datatable keyboard navigation
 */
export class KeyboardNavigationHelper {
  /**
   * Find the next visible row index in the specified direction
   * Handles both grouped and non-grouped tables
   * 
   * @param rowIndex Current row index (number for non-grouped, string for grouped tables)
   * @param tableId Unique table instance ID for DOM queries
   * @param direction Navigation direction ('up' or 'down')
   * @returns Next visible row index, or null if at boundary
   */
  static findNextVisibleRow(
    rowIndex: number | string,
    tableId: string,
    direction: 'up' | 'down'
  ): number | string | null {
    // Check if grouping is enabled by checking if rowIndex is a string
    const isGrouped = typeof rowIndex === 'string';
    
    // For non-grouped tables, use simple arithmetic (faster)
    if (!isGrouped) {
      const targetRow = direction === 'up' ? (rowIndex as number) - 1 : (rowIndex as number) + 1;
      
      // Quick boundary check - ensure target row exists
      const selector = `[data-table-id="${tableId}"] [data-row-index="${targetRow}"]`;
      const targetExists = document.querySelector(selector);
      
      return targetExists ? targetRow : null;
    }
    
    // For grouped tables, query DOM to find visible rows
    const selector = `[data-table-id="${tableId}"] [data-row-index]`;
    const allCells = document.querySelectorAll(selector);
    
    // Extract unique row indices as STRINGS (important for grouped tables)
    const rowIndices = new Set<string>();
    allCells.forEach(cell => {
      const rowIndexAttr = cell.getAttribute('data-row-index');
      if (rowIndexAttr !== null) {
        rowIndices.add(rowIndexAttr);
      }
    });
    
    // Convert to sorted array (handles "0-01", "0-11", "0-21", "1-01" formats)
    // localeCompare with numeric: true handles the hyphen-separated format correctly
    const sortedRows = Array.from(rowIndices).sort((a, b) => {
      return a.localeCompare(b, undefined, { numeric: true });
    });
    
    // Find current row position (convert rowIndex to string)
    const currentRowStr = String(rowIndex);
    const currentPosition = sortedRows.indexOf(currentRowStr);
    
    if (currentPosition === -1) {
      return null;
    }
    
    // Get next/previous row based on direction
    if (direction === 'up') {
      if (currentPosition > 0) {
        return sortedRows[currentPosition - 1];
      }
    } else if (currentPosition < sortedRows.length - 1) { // down
      return sortedRows[currentPosition + 1];
    }
    
    // At boundary
    return null;
  }
}
